import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);

    scheduleHalloweenPromotion(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (isHalloweenGiftVariantId(lineItemRow?.dataset.variantId)) {
      scheduleHalloweenPromotion(0);
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (isHalloweenGiftVariantId(lineItemRow?.dataset.variantId)) {
      scheduleHalloweenPromotion(0);
      return;
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });

        this.#updateCartQuantitySelectorButtonStates();
        scheduleHalloweenPromotion();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    if (!event.promise) scheduleHalloweenPromotion();

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }

        scheduleHalloweenPromotion();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

/**
 * Halloween 2026 — compre X, ganhe Y.
 *
 * A janela usa um offset explícito para continuar baseada no horário de
 * Brasília mesmo quando o navegador do cliente estiver em outro fuso.
 */
const HALLOWEEN_PROMO_START = Date.parse('2026-10-26T00:00:00-03:00');
const HALLOWEEN_PROMO_END_EXCLUSIVE = Date.parse('2026-11-01T00:00:00-03:00');
const HALLOWEEN_PROMO_TEST_KEY = 'delly-halloween-promo-test';
const HALLOWEEN_PROMO_PROPERTY = '_halloween_promo';
const HALLOWEEN_PROMO_THRESHOLD = 35000;
const HALLOWEEN_PROMO_GIFTS = {
  base: 67750755729722,
  aboveThreshold: 67750755664186,
};

let halloweenPromoTimer = null;
let halloweenPromoBoundaryTimer = null;
let halloweenPromoProcessing = false;
let halloweenPromoPending = false;

function readHalloweenTestModeFromUrl() {
  const value = new URLSearchParams(window.location.search).get('promoHalloween');

  try {
    if (value === 'true') sessionStorage.setItem(HALLOWEEN_PROMO_TEST_KEY, 'true');
    if (value === 'false') sessionStorage.removeItem(HALLOWEEN_PROMO_TEST_KEY);
  } catch {
    // sessionStorage pode estar indisponível em modos restritivos do navegador.
  }
}

function isHalloweenTestModeActive() {
  try {
    return sessionStorage.getItem(HALLOWEEN_PROMO_TEST_KEY) === 'true';
  } catch {
    return false;
  }
}

function isHalloweenPromotionActive(now = Date.now()) {
  return (
    isHalloweenTestModeActive() ||
    (now >= HALLOWEEN_PROMO_START && now < HALLOWEEN_PROMO_END_EXCLUSIVE)
  );
}

function isHalloweenGiftLine(item) {
  // Identify by variant as well as by our private property. This prevents the
  // same promotional product from being added as a second, editable cart line
  // through a product URL or another add-to-cart control.
  return isHalloweenGiftVariantId(item.variant_id);
}

function isHalloweenGiftVariantId(variantId) {
  return Object.values(HALLOWEEN_PROMO_GIFTS).some(
    (giftVariantId) => String(giftVariantId) === String(variantId)
  );
}

function removeHalloweenGiftQuantityControls(root = document) {
  const giftVariantIds = Object.values(HALLOWEEN_PROMO_GIFTS);

  for (const variantId of giftVariantIds) {
    const selectorQuery = `cart-quantity-selector-component[data-variant-id="${variantId}"]`;
    const selectors = [
      ...(root instanceof Element && root.matches(selectorQuery) ? [root] : []),
      ...root.querySelectorAll(selectorQuery),
    ];

    for (const selector of selectors) {
      selector.closest('.cart-items__quantity')?.remove();
    }
  }
}

function installHalloweenGiftQuantityProtection() {
  const styleId = 'halloween-gift-quantity-protection';

  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = Object.values(HALLOWEEN_PROMO_GIFTS)
      .map(
        (variantId) =>
          `.cart-items__table-row[data-variant-id="${variantId}"] .cart-items__quantity, ` +
          `.cart-items__quantity:has(cart-quantity-selector-component[data-variant-id="${variantId}"])` +
          '{display:none!important}'
      )
      .join('');
    document.head.appendChild(style);
  }

  removeHalloweenGiftQuantityControls();

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        removeHalloweenGiftQuantityControls(node);
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

function getHalloweenEligibleSubtotal(cart) {
  return cart.items.reduce(
    (total, item) => total + (isHalloweenGiftLine(item) ? 0 : Number(item.final_line_price) || 0),
    0
  );
}

async function getHalloweenCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js?halloween=${Date.now()}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });

  if (!response.ok) throw new Error(`Não foi possível consultar o carrinho (${response.status}).`);
  return response.json();
}

async function addHalloweenGifts(variantIds) {
  if (variantIds.length === 0) return;

  const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', {
    body: JSON.stringify({
      items: variantIds.map((id) => ({
        id,
        quantity: 1,
        properties: { [HALLOWEEN_PROMO_PROPERTY]: 'true' },
      })),
    }),
  }));

  if (!response.ok) throw new Error(`Não foi possível adicionar o brinde de Halloween (${response.status}).`);
}

async function setHalloweenGiftQuantity(lineKey, quantity) {
  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
    body: JSON.stringify({ id: lineKey, quantity }),
  }));

  if (!response.ok) throw new Error(`Não foi possível atualizar o brinde de Halloween (${response.status}).`);
}

async function refreshHalloweenCartSections() {
  const sections = new Map();

  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof CartItemsComponent) sections.set(component.sectionId, component);
  }

  await Promise.allSettled(
    [...sections.values()].map((component) =>
      sectionRenderer.renderSection(component.sectionId, {
        cache: false,
        mode: component.isDrawer ? 'hydration' : 'full',
      })
    )
  );
}

async function processHalloweenPromotion() {
  if (halloweenPromoProcessing) {
    halloweenPromoPending = true;
    return;
  }

  halloweenPromoProcessing = true;

  try {
    const cart = await getHalloweenCart();
    const promoLines = cart.items.filter(isHalloweenGiftLine);
    const desiredVariantIds = new Set();

    if (isHalloweenPromotionActive()) {
      const eligibleSubtotal = getHalloweenEligibleSubtotal(cart);

      if (eligibleSubtotal > 0) desiredVariantIds.add(String(HALLOWEEN_PROMO_GIFTS.base));
      if (eligibleSubtotal > HALLOWEEN_PROMO_THRESHOLD) {
        desiredVariantIds.add(String(HALLOWEEN_PROMO_GIFTS.aboveThreshold));
      }
    }

    let cartChanged = false;
    const retainedVariants = new Set();

    for (const line of promoLines) {
      const variantId = String(line.variant_id);
      const shouldKeep = desiredVariantIds.has(variantId) && !retainedVariants.has(variantId);

      if (shouldKeep) {
        retainedVariants.add(variantId);
        if (Number(line.quantity) !== 1) {
          await setHalloweenGiftQuantity(line.key, 1);
          cartChanged = true;
        }
      } else {
        await setHalloweenGiftQuantity(line.key, 0);
        cartChanged = true;
      }
    }

    const missingVariantIds = [...desiredVariantIds]
      .filter((variantId) => !retainedVariants.has(variantId))
      .map(Number);

    if (missingVariantIds.length > 0) {
      await addHalloweenGifts(missingVariantIds);
      cartChanged = true;
    }

    if (cartChanged) {
      await refreshHalloweenCartSections();
      document.dispatchEvent(new CustomEvent('halloween-promo:updated', { bubbles: true }));
    }

    return true;
  } catch (error) {
    console.error('[Halloween Promo] Erro ao sincronizar brindes:', error);
    return false;
  } finally {
    halloweenPromoProcessing = false;

    if (halloweenPromoPending) {
      halloweenPromoPending = false;
      scheduleHalloweenPromotion(0);
    }
  }
}

function scheduleHalloweenPromotion(delay = 150) {
  if (halloweenPromoTimer) clearTimeout(halloweenPromoTimer);
  halloweenPromoTimer = setTimeout(() => {
    halloweenPromoTimer = null;
    processHalloweenPromotion();
  }, delay);
}

function scheduleHalloweenBoundaryCheck() {
  if (halloweenPromoBoundaryTimer) clearTimeout(halloweenPromoBoundaryTimer);

  const now = Date.now();
  const nextBoundary = now < HALLOWEEN_PROMO_START
    ? HALLOWEEN_PROMO_START
    : now < HALLOWEEN_PROMO_END_EXCLUSIVE
      ? HALLOWEEN_PROMO_END_EXCLUSIVE
      : null;

  if (nextBoundary === null) return;

  const maximumTimeout = 2147483647;
  halloweenPromoBoundaryTimer = setTimeout(() => {
    scheduleHalloweenPromotion(0);
    scheduleHalloweenBoundaryCheck();
  }, Math.min(Math.max(nextBoundary - now + 50, 0), maximumTimeout));
}

function isAcceleratedCheckoutClick(event) {
  return event.composedPath().some(
    (node) =>
      node instanceof Element &&
      (node.matches('.shopify-payment-button__button--unbranded') ||
        node.matches('shopify-buy-it-now-button'))
  );
}

async function synchronizeHalloweenPromotionBeforeCheckout() {
  // An initial/cart-change synchronization may already be running. Waiting for
  // it avoids treating the lock's early return as a failed checkout preparation.
  while (halloweenPromoProcessing) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  return processHalloweenPromotion();
}

async function handleHalloweenAcceleratedCheckout(event) {
  if (!isHalloweenPromotionActive() || !isAcceleratedCheckoutClick(event)) return;

  const path = event.composedPath();
  const productFormComponent = path.find(
    (node) => node instanceof Element && node.matches('product-form-component')
  );
  const form = productFormComponent?.querySelector('form[data-type="add-to-cart-form"]');

  // If Shopify changes the accelerated-checkout markup, leave its native
  // behavior intact instead of preventing checkout without a usable form.
  if (!(form instanceof HTMLFormElement)) return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const checkoutContainer = form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
  if (checkoutContainer?.getAttribute('aria-busy') === 'true') return;

  checkoutContainer?.setAttribute('aria-busy', 'true');
  if (checkoutContainer instanceof HTMLElement) checkoutContainer.style.pointerEvents = 'none';

  try {
    const formData = new FormData(form);
    const addResponse = await fetch(
      Theme.routes.cart_add_url,
      fetchConfig('javascript', { body: formData })
    );

    if (!addResponse.ok) {
      const error = await addResponse.json().catch(() => ({}));
      throw new Error(error.description || error.message || `Erro ${addResponse.status} ao adicionar o produto.`);
    }

    const promotionReady = await synchronizeHalloweenPromotionBeforeCheckout();
    if (!promotionReady) throw new Error('Não foi possível preparar os brindes antes do checkout.');

    window.location.assign('/checkout');
  } catch (error) {
    console.error('[Halloween Promo] Não foi possível preparar o checkout:', error);
    window.alert(error instanceof Error ? error.message : 'Não foi possível iniciar o checkout. Tente novamente.');

    checkoutContainer?.removeAttribute('aria-busy');
    if (checkoutContainer instanceof HTMLElement) checkoutContainer.style.pointerEvents = '';
  }
}

readHalloweenTestModeFromUrl();
scheduleHalloweenBoundaryCheck();
installHalloweenGiftQuantityProtection();

// Capture is required because Shopify's accelerated button is a web component
// and otherwise starts checkout before the promotion can synchronize the cart.
document.addEventListener('click', handleHalloweenAcceleratedCheckout, true);

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
